import React, { useState } from 'react';
import { X, ChevronLeft, MapPin, Star, DollarSign, ExternalLink } from 'lucide-react';
import { stockPhoto } from '../../assets/stock/index.ts';

type FacilitySortBy = 'distance' | 'price' | 'rating';

interface AgedCareFacilityDailyFees {
    basic: number;
    dap: number;
    nccc: number;
    hsc: number;
    extraServices: number;
}

interface AgedCareFacility {
    id: number;
    name: string;
    suburb: string;
    state: string;
    radPrice: number;
    dailyFees: AgedCareFacilityDailyFees;
    distance: number;
    rating: number;
    careLevels: string[];
    amenities: string[];
    websiteUrl: string;
    images: string[];
}

interface AgedCareFacilitySearchProps {
    onClose: () => void;
}

const AgedCareFacilitySearch = ({ onClose }: AgedCareFacilitySearchProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [maxDistance, setMaxDistance] = useState(10);
    const [sortBy, setSortBy] = useState<FacilitySortBy>('distance');
    const [showResults, setShowResults] = useState(false);

    // Mock aged care facility data (standalone version)
    const mockFacilities: AgedCareFacility[] = [
        {
            id: 1,
            name: "Sunrise Aged Care",
            suburb: "Croydon",
            state: "NSW",
            radPrice: 500000,
            dailyFees: {
                basic: 70,
                dap: 68,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 1.2,
            rating: 4.2,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Garden", "Library", "Chapel"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/sunrise-aged-care',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 300, 200),
                stockPhoto('1586023492125-27b2c045efd7', 300, 200),
                stockPhoto('1560448204-e02f11c3d0e2', 300, 200)
            ]
        },
        {
            id: 2,
            name: "Garden View Care Centre",
            suburb: "Five Dock",
            state: "NSW",
            radPrice: 850000,
            dailyFees: {
                basic: 70,
                dap: 116,
                nccc: 0,
                hsc: 13,
                extraServices: 15
            },
            distance: 2.1,
            rating: 4.5,
            careLevels: ["Low Care", "High Care", "Dementia Care"],
            amenities: ["Swimming Pool", "Gym", "Café"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/garden-view-care-centre',
            images: [
                stockPhoto('1571896349842-33c89424de2d', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200)
            ]
        },
        {
            id: 3,
            name: "Heritage Manor",
            suburb: "Campsie",
            state: "NSW",
            radPrice: 780000,
            dailyFees: {
                basic: 70,
                dap: 107,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 3.4,
            rating: 3.8,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Garden", "Library"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/heritage-manor',
            images: [
                stockPhoto('1512917774080-9991f1c4c750', 300, 200),
                stockPhoto('1560448204-603b3fc33ddc', 300, 200),
                stockPhoto('1564013799919-ab600027ffc6', 300, 200)
            ]
        },
        {
            id: 4,
            name: "Riverside Care Centre",
            suburb: "Burwood",
            state: "NSW",
            radPrice: 650000,
            dailyFees: {
                basic: 70,
                dap: 89,
                nccc: 0,
                hsc: 13,
                extraServices: 8
            },
            distance: 4.7,
            rating: 4.0,
            careLevels: ["Low Care", "High Care", "Palliative Care"],
            amenities: ["Water Views", "Garden", "Chapel"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/riverside-care-centre',
            images: [
                stockPhoto('1564013799919-ab600027ffc6', 300, 200),
                stockPhoto('1582407947304-fd86f028f716', 300, 200),
                stockPhoto('1571896349842-33c89424de2d', 300, 200)
            ]
        },
        {
            id: 5,
            name: "Mountain View Aged Care",
            suburb: "Ashfield",
            state: "NSW",
            radPrice: 420000,
            dailyFees: {
                basic: 70,
                dap: 58,
                nccc: 0,
                hsc: 13,
                extraServices: 0
            },
            distance: 2.8,
            rating: 4.1,
            careLevels: ["Low Care", "High Care"],
            amenities: ["Garden", "Library", "Chapel"],
            websiteUrl: 'https://example.org/carecompanion-demo/facilities/mountain-view-aged-care',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 300, 200),
                stockPhoto('1586023492125-27b2c045efd7', 300, 200),
                stockPhoto('1560448204-e02f11c3d0e2', 300, 200)
            ]
        }
    ];

    const filteredFacilities = mockFacilities
        .filter(facility => facility.distance <= maxDistance)
        .sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return a.radPrice - b.radPrice;
                case 'rating':
                    return b.rating - a.rating;
                default:
                    return a.distance - b.distance;
            }
        });

    const handleSearch = () => {
        if (searchTerm.trim()) {
            setShowResults(true);
        }
    };

    const handleBackToSearch = () => {
        setShowResults(false);
        setSearchTerm('');
    };

    const handleACATApplication = () => {
        window.open('https://www.myagedcare.gov.au/assessment/apply-online', '_blank');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {showResults ? `Aged Care Facilities near ${searchTerm}` : 'Find Aged Care Facilities'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="p-6">
                    {!showResults ? (
                        // Search Interface
                        <div className="space-y-6">
                            <div className="text-center">
                                <p className="text-gray-600 mb-6">Enter your suburb or area to find nearby aged care facilities</p>
                            </div>
                            
                            <div className="max-w-md mx-auto space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Suburb or Area</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="e.g., Croydon, Five Dock, Campsie..."
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Distance: {maxDistance}km</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="20"
                                        value={maxDistance}
                                        onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                </div>
                                
                                <button
                                    onClick={handleSearch}
                                    disabled={!searchTerm.trim()}
                                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-lg font-semibold"
                                >
                                    Search Facilities
                                </button>
                            </div>

                            {/* ACAT Application Section */}
                            <div className="mt-8 p-6 bg-white rounded-lg border border-blue-200">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                                            <ExternalLink className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2">Need an aged care needs assessment for residential care?</h3>
                                        <p className="text-blue-800 mb-4">
                                            To access residential aged care, you need an aged care needs assessment. 
                                            This determines your care needs and eligibility for government-subsidised care.
                                        </p>
                                        <button
                                            onClick={handleACATApplication}
                                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
                                        >
                                            <ExternalLink size={16} />
                                            Apply via My Aged Care
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Results Interface
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleBackToSearch}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <ChevronLeft size={20} />
                                    Back to Search
                                </button>
                                
                                <div className="flex items-center gap-4">
                                    <label className="text-sm font-medium text-gray-700">Sort By:</label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value as FacilitySortBy)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="distance">Distance</option>
                                        <option value="price">RAD Price</option>
                                        <option value="rating">Rating</option>
                                    </select>
                                </div>
                            </div>

                            {/* Results */}
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {filteredFacilities.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No facilities found matching your criteria.
                                    </div>
                                ) : (
                                    filteredFacilities.map(facility => (
                                        <div
                                            key={facility.id}
                                            className="border border-gray-200 rounded-xl p-4 md:p-5 hover:border-blue-300 hover:shadow-md transition-all"
                                        >
                                            <div className="flex flex-col md:flex-row gap-5">
                                                <div className="shrink-0 w-full md:w-[min(100%,280px)] md:max-w-[280px]">
                                                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200/80">
                                                        {facility.images?.[0] ? (
                                                            <img
                                                                src={facility.images[0]}
                                                                alt={facility.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">No photo</div>
                                                        )}
                                                    </div>
                                                    {facility.images && facility.images.length > 1 ? (
                                                        <div className="flex gap-1.5 mt-2">
                                                            {facility.images.slice(1, 4).map((image, index) => (
                                                                <img
                                                                    key={index}
                                                                    src={image}
                                                                    alt={`${facility.name} view ${index + 2}`}
                                                                    className="h-14 min-w-0 flex-1 object-cover rounded-lg border border-slate-200"
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{facility.name}</h3>
                                                        <p className="text-gray-600 mt-0.5">{facility.suburb}, {facility.state}</p>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-600">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <MapPin size={15} className="text-sky-600 shrink-0" aria-hidden />
                                                                {facility.distance}km away
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <Star size={15} className="text-amber-500 shrink-0" aria-hidden />
                                                                {facility.rating}/5
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                                                                <DollarSign size={15} className="text-emerald-600 shrink-0" aria-hidden />
                                                                ${facility.radPrice.toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="mt-3">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Care levels</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{facility.careLevels.join(', ')}</p>
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amenities</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{facility.amenities.join(', ')}</p>
                                                        </div>
                                                        {facility.websiteUrl ? (
                                                            <a
                                                                href={facility.websiteUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                                                            >
                                                                <ExternalLink size={14} aria-hidden />
                                                                Visit website
                                                                <span className="text-xs font-normal text-gray-500">(demo URL)</span>
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                    <div className="shrink-0 flex lg:flex-col justify-end lg:justify-center gap-2 lg:w-44">
                                                        <button
                                                            type="button"
                                                            onClick={() => window.open('https://www.myagedcare.gov.au/find-a-provider', '_blank')}
                                                            className="w-full lg:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold shadow-sm"
                                                        >
                                                            Contact Facility
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgedCareFacilitySearch;


